/**
 * The browser-bound half of the renderer decision: where the three inputs
 * `renderer-mode.ts` reasons over actually come from, and the one place that
 * writes the stored verdict back.
 *
 * Separate from `renderer-mode.ts` so that module can keep its claim of being
 * pure — every branch of `decideRendererMode` is assertable with hand-written
 * inputs, and none of them needs a `location`, a `navigator` or a
 * `localStorage` to exist. This file is the two-line adapter that supplies them,
 * and it is deliberately the only thing in the feature that reads a global.
 *
 * It is in `three/` rather than in `flight/` because it has **two** consumers
 * that share nothing else: the flight scene, which uses the plan to build a
 * renderer, and the PREFERENCES screen, which uses it to print a fact. Putting
 * it under either one would have made the other import across the app.
 */

import { browserSettingsStore } from "../audio/settings.ts";
import {
  decideRendererMode,
  factsOf,
  loadRendererFallback,
  NO_RENDERER_FALLBACK,
  type RendererFacts,
  type RendererFallbackRecord,
  type RendererPlan,
  saveRendererFallback,
} from "./renderer-mode.ts";
import { hasWebGpu } from "./webgpu-support.ts";

/** `location.search`, or `undefined` where there is no location — a test, a worker, an SSR pass. */
function currentSearch(): string | undefined {
  return typeof location === "undefined" ? undefined : location.search;
}

/**
 * What the **next** renderer built in this document will be, and why.
 *
 * Called once per flight (`flight-scene.ts`) and once per PREFERENCES paint.
 * Deliberately not memoised: the stored record changes underneath it — a failed
 * boot writes one, `?renderer=webgpu` clears one — and a PREFERENCES screen
 * opened after a fallback must print what happened rather than what was true
 * when the module was first imported.
 */
export function currentRendererPlan(): RendererPlan {
  return decideRendererMode({
    search: currentSearch(),
    stored: loadRendererFallback(browserSettingsStore()),
    hasWebGpu: hasWebGpu(),
  });
}

/** The plan as the two fields a screen prints, with the side-effect flags dropped. */
export function plannedRendererFacts(): RendererFacts {
  return factsOf(currentRendererPlan());
}

/** Records a driver failure so the next boot comes up on WebGL without paying for the probe again. */
export function rememberRendererFallback(record: RendererFallbackRecord): void {
  saveRendererFallback(browserSettingsStore(), record);
}

/** `?renderer=webgpu`'s side of the bargain: forget the failure and let the next boot try again. */
export function forgetRendererFallback(): void {
  saveRendererFallback(browserSettingsStore(), NO_RENDERER_FALLBACK);
}
